import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {TNOidc, useOidcExpirationTime} from '@touchnet/react-oidc';
import React, {ReactNode, useEffect} from 'react';
import {Alert} from 'reactstrap';
import WhiteoutWrapper from './components/general/whiteout';
import {useSelector} from "react-redux";
import Selectors from "~/state/selectors.ts";
import {useLocation, useNavigate} from "react-router-dom";
import {isOidcCallbackStillInUrl} from "~/utils/url-utils";
import {API_KEY_PATH_RE, APIKEY_HASH_RE, POST_LOGIN_REDIRECT_APIKEY_HASH} from "~/constants";

interface ComponentProps {
    children: ReactNode | undefined;
}

// Redirects to the captured target path after the user logs in successfully
export const PostLoginRedirectGuard: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const exp = useOidcExpirationTime();
    const isAuthenticated = typeof exp === "number" && exp > Date.now();

    const isCallbackStillPresent = isOidcCallbackStillInUrl(window.location.search);

    useEffect(() => {

        if (!isAuthenticated) return;
        if (isCallbackStillPresent) return;

        const savedHash: string | null = sessionStorage.getItem(POST_LOGIN_REDIRECT_APIKEY_HASH);
        if (!savedHash || !APIKEY_HASH_RE.test(savedHash) || savedHash.includes("//")) {
            sessionStorage.removeItem(POST_LOGIN_REDIRECT_APIKEY_HASH);
            return;
        }
        const targetPath = savedHash.replace(/^#/, "")

        if (!API_KEY_PATH_RE.test(targetPath) || location.pathname === targetPath) {
            sessionStorage.removeItem(POST_LOGIN_REDIRECT_APIKEY_HASH);
            return;
        }
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_APIKEY_HASH);
        navigate(targetPath, {replace: true});
    }, [isAuthenticated, isCallbackStillPresent, location.pathname, navigate]);
    return null;
}

export const TNOidcWrapper = ({children}: ComponentProps): JSX.Element => {
    const appEnvState = useSelector(Selectors.getAppEnv);
    const env = appEnvState.env;

    const oidcFailureElement = (
        <WhiteoutWrapper>
            <Alert color="danger">
                <strong>
                    <FontAwesomeIcon className="me-1" icon="info-circle" />
                    Error!
                </strong>
                <span> An error occurred fetching login information. Please contact your administrator.</span>
            </Alert>
        </WhiteoutWrapper>
    );

    const loginInProcessElement = (
        <WhiteoutWrapper>Loading</WhiteoutWrapper>
    );

    return (
        <TNOidc
            urls={env.oidc.urls}
            clientId={env.oidc.clientId}
            oidcFailureElement={oidcFailureElement}
            loginInProcessElement={loginInProcessElement}
        >
            <PostLoginRedirectGuard/>
            {children}
        </TNOidc>
    );
};

export default TNOidcWrapper;